import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useWorkspaceStore } from '@/shared/stores/workspace.store';
import { useTranslation } from 'react-i18next';
import { formatMinutes, formatRelative } from '@/shared/lib/intl';
import { ROUTES } from '@/shared/constants/routes';
import type { Post, PostResult } from '@/shared/services/posts.service';
import { attentionService, type AttentionData } from '@/shared/services/attention.service';
import type { PublishErrorAction } from '@unipostudio/shared-types';
import { describePublishFailure, isFailedResult, postRetryIsSafe } from '@/shared/lib/post-result-error';
import type { ReadinessAction } from '@unipostudio/shared-validators';
import { goesOutText, postRisk, readinessActionLabel, readinessIssueText } from '@/shared/lib/readiness-text';
import { readinessActionRoute } from '@/shared/hooks/useReadinessAction';

export type AttentionKind =
  | 'post-at-risk'
  | 'failed-posts'
  | 'post-gone'
  | 'token-dead'
  | 'token-expiring'
  | 'pending-approval'
  | 'stuck-media'
  | 'sla-breach'
  | 'knowledge-gap';

/** Blueprint S30 groups, in display order. */
export type AttentionGroup = 'risk' | 'failed' | 'tokens' | 'approvals' | 'sla' | 'media' | 'knowledge';
/** "Posts at risk" (Task 1.2) leads: it is the one group still preventable. */
export const ATTENTION_GROUPS: readonly AttentionGroup[] = ['risk', 'failed', 'tokens', 'approvals', 'sla', 'media', 'knowledge'];

/** The one action a row offers. `to` is always a route; the page may run a faster path first. */
export interface AttentionAction {
  kind: 'retry' | 'reconnect' | 'review' | 'open' | 'add-knowledge' | 'go' | 'publish-failure' | 'readiness' | 'receipt';
  to: string;
  postId?: string;
  platform?: string;
  /** A failed publish's own action (PUBLISH_ERROR_COPY), run by usePublishFailureAction. */
  failure?: PublishErrorAction;
  /** A pre-publish issue's own action (Task 1.2), run by useReadinessAction. */
  readiness?: ReadinessAction;
  conversationKey?: string;
  /** The unanswered question, for a knowledge-gap row. */
  text?: string;
}

export interface AttentionItem {
  key: string;
  kind: AttentionKind;
  group: AttentionGroup;
  label: string;
  detail: string;
  tone: 'error' | 'warning';
  actionLabel: string;
  /** In-app route that fixes it. Same as `action.to`. */
  to: string;
  action: AttentionAction;
  /** Some rows are facts (a dead token) rather than noise; those cannot be dismissed. */
  dismissable: boolean;
}

const PLATFORM_NAMES: Record<string, string> = {
  instagram: 'Instagram', facebook: 'Facebook', twitter: 'X (Twitter)', linkedin: 'LinkedIn', whatsapp: 'WhatsApp',
  youtube: 'YouTube', pinterest: 'Pinterest', telegram: 'Telegram', tiktok: 'TikTok', google_business: 'Google Business', zetpin: 'Zetpin',
};

const preview = (text: string, max = 80): string => (text.length > max ? `${text.slice(0, max - 1)}…` : text);
const platformName = (platform: string): string => PLATFORM_NAMES[platform.toLowerCase()] ?? platform;

/** The first failed channel of a post that did not publish. */
function failedResult(post: Post): PostResult | null {
  // results is optional, its status is the Prisma enum (FAILED, not failed),
  // and the message is errorMessage. A post deleted from its platform keeps
  // a FAILED result: that is not a failure.
  return (post.results ?? []).find(isFailedResult) ?? null;
}

/**
 * Query key of GET /attention. It sits under `posts`, so every post change
 * that refreshes the post lists (publish, retry, approve, delete, realtime)
 * refreshes the Attention list and the badge with them.
 */
export const ATTENTION_KEY = ['posts', 'attention'] as const;

const attentionKey = (workspaceId: string | undefined): QueryKey => [...ATTENTION_KEY, workspaceId];

/**
 * GET /attention for the brand in view: one request shared by the sidebar
 * and phone badges, the Attention page and the Overview (F19), so the count
 * on every one of them is the same number.
 */
export function useAttentionData() {
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  return useQuery({
    queryKey: attentionKey(workspaceId),
    queryFn: () => attentionService.get(),
    enabled: !!workspaceId,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Per-group sizes as the page's chips and headers show them: the true counts, not the rows listed. */
export type AttentionGroupTotals = Record<AttentionGroup, number>;

export interface AttentionItems {
  items: AttentionItem[];
  /** Everything that needs somebody: the page title, the badge and the Overview tile. */
  total: number;
  groupTotals: AttentionGroupTotals;
  /** Questions the AI could not answer on a plan without the knowledge base (F20); 0 otherwise. */
  knowledgeLocked: number;
  isLoading: boolean;
}

/**
 * The rows of the Attention list, one per problem, grouped: posts at risk
 * before they go out, failed publishes, dead or expiring connections, posts
 * pending approval, SLA breaches, stuck media, knowledge gaps. Built from
 * GET /attention, which holds the true totals and the brand's dismissals; a
 * group with more than the rows listed ends with one row saying how many.
 */
export function useAttentionItems(): AttentionItems {
  const { t } = useTranslation();
  const query = useAttentionData();
  const data = query.data;

  const items = useMemo<AttentionItem[]>(() => (data ? buildAttentionItems(data, t) : []), [data, t]);
  const groupTotals = useMemo<AttentionGroupTotals>(() => attentionGroupTotals(data), [data]);

  return {
    items,
    total: data?.total ?? 0,
    groupTotals,
    knowledgeLocked: data && !data.knowledge.allowed ? data.knowledge.total : 0,
    isLoading: query.isLoading,
  };
}

/** The Attention badge in the sidebar and the phone tab bar: the same total as the page and the Overview. */
export function useAttentionCount(): number {
  return useAttentionData().data?.total ?? 0;
}

export function attentionGroupTotals(data: AttentionData | undefined): AttentionGroupTotals {
  return {
    risk: data?.risk.total ?? 0,
    failed: (data?.failed.total ?? 0) + (data?.gone.total ?? 0),
    tokens: data?.tokens.total ?? 0,
    approvals: data?.approvals?.total ?? 0,
    sla: data?.sla.total ?? 0,
    media: data?.media.total ?? 0,
    knowledge: data?.knowledge.allowed ? data.knowledge.total : 0,
  };
}

/** Which group a dismissal key belongs to, to take one off its count at once. */
function groupOfKey(key: string): keyof Pick<AttentionData, 'risk' | 'failed' | 'gone' | 'tokens' | 'media' | 'knowledge'> | null {
  if (key.startsWith('risk-')) return 'risk';
  if (key.startsWith('failed-')) return 'failed';
  if (key.startsWith('gone-')) return 'gone';
  if (key.startsWith('expiring-')) return 'tokens';
  if (key.startsWith('media-')) return 'media';
  if (key.startsWith('gap-')) return 'knowledge';
  return null;
}

/** GET /attention's answer with one row dismissed: gone from its group and from every count. */
export function withoutDismissed(data: AttentionData, key: string, expiresAt: string): AttentionData {
  const next: AttentionData = { ...data, dismissed: { ...data.dismissed, [key]: expiresAt } };
  const group = groupOfKey(key);
  if (!group || data.dismissed[key]) return next;
  const id = key.slice(key.indexOf('-') + 1);
  const counted = group !== 'knowledge' || data.knowledge.allowed;
  switch (group) {
    case 'risk':
    case 'failed':
      next[group] = { total: Math.max(0, data[group].total - 1), items: data[group].items.filter((p) => p.id !== id) };
      break;
    case 'gone':
      next.gone = { ...data.gone, total: Math.max(0, data.gone.total - 1) };
      break;
    case 'tokens':
      next.tokens = { total: Math.max(0, data.tokens.total - 1), items: data.tokens.items.filter((r) => `expiring-${r.platform}` !== key) };
      break;
    case 'media':
      next.media = { total: Math.max(0, data.media.total - 1), items: data.media.items.filter((m) => m.id !== id) };
      break;
    case 'knowledge':
      next.knowledge = { ...data.knowledge, total: Math.max(0, data.knowledge.total - 1), items: data.knowledge.items.filter((g) => g.key !== key) };
      break;
  }
  if (counted) next.total = Math.max(0, data.total - 1);
  return next;
}

/**
 * Dismiss (blueprint S30): quiets a row for the whole brand for 24 hours,
 * stored on the server, so teammates and other devices see the same list.
 * The row leaves the list and every count at once; a failure puts it back.
 */
export function useDismissAttention(): { dismiss: (key: string) => Promise<void>; isPending: boolean } {
  const qc = useQueryClient();
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspace?.id);
  const key = attentionKey(workspaceId);
  const mutation = useMutation({
    mutationFn: (rowKey: string) => attentionService.dismiss(rowKey),
    onMutate: async (rowKey: string) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<AttentionData>(key);
      if (previous) qc.setQueryData<AttentionData>(key, withoutDismissed(previous, rowKey, new Date(Date.now() + 24 * 3_600_000).toISOString()));
      return { previous };
    },
    onError: (_err, _rowKey, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: key });
    },
  });
  const { mutateAsync } = mutation;
  const dismiss = useCallback(async (rowKey: string): Promise<void> => {
    await mutateAsync(rowKey);
  }, [mutateAsync]);
  return { dismiss, isPending: mutation.isPending };
}

type Translate = ReturnType<typeof useTranslation>['t'];

/** One row past the listed ones of a group, saying how many more there are. */
function moreRow(group: AttentionGroup, kind: AttentionKind, count: number, to: string, t: Translate): AttentionItem {
  return {
    key: `${group}-more`,
    kind,
    group,
    label: t('attention.moreOther', { count }),
    detail: t('attention.moreOtherDetail'),
    tone: kind === 'failed-posts' || kind === 'sla-breach' ? 'error' : 'warning',
    actionLabel: t('attention.open'),
    to,
    action: { kind: 'go', to },
    dismissable: false,
  };
}

/** The rows of GET /attention's answer, worded for the reader. Pure, for the hook and its tests. */
export function buildAttentionItems(data: AttentionData, t: Translate): AttentionItem[] {
  const out: AttentionItem[] = [];
  const dismissed = data.dismissed;

  // Soonest first: the closer its time, the less time to fix it.
  const risky = [...data.risk.items].sort((a, b) => new Date(a.scheduledAt ?? 0).getTime() - new Date(b.scheduledAt ?? 0).getTime());
  let riskRows = 0;
  for (const post of risky) {
    const risk = postRisk(post);
    const issue = risk?.issues[0];
    if (!risk || !issue) continue;
    riskRows += 1;
    const when = goesOutText(post.scheduledAt, t);
    const reason = readinessIssueText(issue, t);
    const to = readinessActionRoute(issue.action, { postId: post.id, platform: issue.platform });
    out.push({
      key: `risk-${post.id}`,
      kind: 'post-at-risk',
      group: 'risk',
      label: when ? t('attention.riskLine', { when, reason }) : reason,
      detail: preview(post.content, 80) || t('attention.untitledPost'),
      tone: risk.status === 'blocked' ? 'error' : 'warning',
      actionLabel: readinessActionLabel(issue, t),
      to,
      action: { kind: 'readiness', to, postId: post.id, platform: issue.platform ?? undefined, readiness: issue.action },
      // A post that will not go out is a fact, not noise.
      dismissable: risk.status !== 'blocked',
    });
  }
  if (data.risk.total > riskRows && riskRows > 0) out.push(moreRow('risk', 'post-at-risk', data.risk.total - riskRows, ROUTES.CALENDAR, t));

  // Newest failures first, failed and partly published together. A failed
  // post is retried, never opened in the composer (it cannot edit it):
  // the fallback is the posts list filtered to it.
  const failing = [...data.failed.items].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  for (const post of failing) {
    const fr = failedResult(post);
    const to = `${ROUTES.POSTS}?status=${post.status}`;
    // Worded by its code, as everywhere else (Task 1.1): the title leads, the reason follows.
    const failure = fr ? describePublishFailure(fr, t) : null;
    // Retry resends every failed channel: never while one may already have the post.
    const coded = failure?.copy.action ?? 'retry';
    const safe = postRetryIsSafe(post.results);
    const action: PublishErrorAction = (coded === 'retry' || coded === 'wait') && !safe ? 'edit_post' : coded;
    const retries = action === 'retry' || action === 'wait';
    out.push({
      key: `failed-${post.id}`,
      kind: 'failed-posts',
      group: 'failed',
      label: failure ? t('attention.failedOn', { platform: failure.title, caption: preview(post.content, 48) }) : preview(post.content, 60),
      // A code the app has no words for points at the post, where "Details" holds the channel's message.
      detail: failure && failure.copy.copy !== 'unknown'
        ? failure.reason
        : post.status === 'PARTIAL' ? t('attention.partialDetail') : t('attention.failedPostsDetail'),
      tone: 'error',
      actionLabel: retries ? t('attention.retry') : t(`publishError.action.${action}`, { platform: platformName(fr?.platform ?? '') }),
      to,
      action: retries || !fr
        ? { kind: 'retry', to, postId: post.id }
        : { kind: 'publish-failure', to, postId: post.id, platform: fr.platform, failure: action },
      dismissable: true,
    });
  }
  let goneRows = 0;
  for (const post of data.gone.items) {
    for (const r of post.results ?? []) {
      if (r.status !== 'PUBLISHED' || r.verification !== 'gone') continue;
      const key = `gone-${post.id}-${r.platform}`;
      if (dismissed[key]) continue;
      goneRows += 1;
      const to = `${ROUTES.POSTS}?receipt=${post.id}`;
      out.push({
        key,
        kind: 'post-gone',
        group: 'failed',
        label: t('attention.goneLabel', { platform: platformName(r.platform), caption: preview(post.content, 48) }),
        detail: t('attention.goneDetail'),
        tone: 'error',
        actionLabel: t('receipt.open'),
        to,
        action: { kind: 'receipt', to, postId: post.id },
        dismissable: true,
      });
    }
  }
  // The lists hold the first rows only: the true count is said, not hidden.
  const failedMore = data.failed.total - failing.length + Math.max(0, data.gone.total - goneRows);
  if (failedMore > 0) {
    out.push({
      key: 'failed-more',
      kind: 'failed-posts',
      group: 'failed',
      label: t('attention.moreFailed', { count: failedMore }),
      detail: t('attention.moreFailedDetail'),
      tone: 'error',
      actionLabel: t('attention.open'),
      to: `${ROUTES.POSTS}?status=FAILED`,
      action: { kind: 'go', to: `${ROUTES.POSTS}?status=FAILED` },
      dismissable: false,
    });
  }

  for (const s of data.tokens.items) {
    const platform = platformName(s.platform);
    if (s.state === 'expired') {
      out.push({
        key: `token-${s.platform}`,
        kind: 'token-dead',
        group: 'tokens',
        label: t('attention.disconnected', { platform }),
        detail: s.connectState === 'REVOKED' ? t('attention.revokedDetail') : t('attention.expiredDetail'),
        tone: 'error',
        actionLabel: t('attention.reconnect'),
        to: ROUTES.CHANNELS,
        action: { kind: 'reconnect', to: ROUTES.CHANNELS, platform: s.platform },
        dismissable: false,
      });
    } else {
      const key = `expiring-${s.platform}`;
      if (dismissed[key]) continue;
      const days = s.daysLeft ?? 0;
      out.push({
        key,
        kind: 'token-expiring',
        group: 'tokens',
        label: days <= 0 ? t('attention.expiresToday', { platform }) : t('attention.expiresIn', { platform, count: days }),
        detail: t('attention.expiresDetail'),
        tone: 'warning',
        actionLabel: t('attention.reconnect'),
        to: ROUTES.CHANNELS,
        action: { kind: 'reconnect', to: ROUTES.CHANNELS, platform: s.platform },
        dismissable: true,
      });
    }
  }

  // Approvals are Pro and up: the server sends none below.
  if (data.approvals) {
    for (const post of data.approvals.items) {
      out.push({
        key: `pending-${post.id}`,
        kind: 'pending-approval',
        group: 'approvals',
        label: preview(post.content, 60) || t('attention.untitledPost'),
        detail: post.scheduledAt ? t('attention.pendingScheduled', { when: formatRelative(post.scheduledAt) }) : t('attention.pendingApprovalDetail'),
        tone: 'warning',
        actionLabel: t('attention.review'),
        to: ROUTES.APPROVALS,
        action: { kind: 'review', to: ROUTES.APPROVALS, postId: post.id },
        dismissable: false,
      });
    }
    const pendingShown = data.approvals.items.length;
    if (data.approvals.total > pendingShown) {
      out.push({
        key: 'pending-more',
        kind: 'pending-approval',
        group: 'approvals',
        label: t('attention.morePending', { n: data.approvals.total - pendingShown }),
        detail: t('attention.morePendingDetail'),
        tone: 'warning',
        actionLabel: t('attention.open'),
        to: ROUTES.APPROVALS,
        action: { kind: 'go', to: ROUTES.APPROVALS },
        dismissable: false,
      });
    }
  }

  for (const c of data.sla.items) {
    const to = `${ROUTES.INBOX}?conversation=${encodeURIComponent(c.conversationKey)}`;
    out.push({
      key: `sla-${c.conversationKey}`,
      kind: 'sla-breach',
      group: 'sla',
      label: t('attention.slaBreached', { name: c.peer || t('attention.customer') }),
      detail: t('attention.slaBreachedDetail', { level: c.levelName || t('inbox.ladder.ai'), late: formatMinutes(c.minutesLate) }),
      tone: 'error',
      actionLabel: t('attention.open'),
      to,
      action: { kind: 'open', to, conversationKey: c.conversationKey },
      dismissable: false,
    });
  }
  if (data.sla.total > data.sla.items.length) {
    out.push(moreRow('sla', 'sla-breach', data.sla.total - data.sla.items.length, `${ROUTES.INBOX}?view=sla_at_risk`, t));
  }

  for (const a of data.media.items) {
    out.push({
      key: `media-${a.id}`,
      kind: 'stuck-media',
      group: 'media',
      label: a.state === 'failed' ? t('attention.mediaFailed', { name: a.name }) : t('attention.mediaStuck', { name: a.name }),
      detail: t('attention.stuckMediaDetail'),
      tone: 'warning',
      actionLabel: t('attention.openLibrary'),
      to: ROUTES.MEDIA,
      action: { kind: 'go', to: ROUTES.MEDIA },
      dismissable: true,
    });
  }
  if (data.media.total > data.media.items.length) {
    out.push(moreRow('media', 'stuck-media', data.media.total - data.media.items.length, ROUTES.MEDIA, t));
  }

  // Knowledge gaps only where the plan has the knowledge base (F20): elsewhere
  // "Add to knowledge" would open a lock. The page shows one upgrade note instead.
  if (data.knowledge.allowed) {
    for (const gap of data.knowledge.items) {
      const question = preview(gap.question, 90);
      const to = `${ROUTES.INBOX_SETTINGS}?gap=${encodeURIComponent(gap.question)}`;
      out.push({
        key: gap.key,
        kind: 'knowledge-gap',
        group: 'knowledge',
        label: t('attention.knowledgeGap', { question }),
        detail: t('attention.knowledgeGapDetail', { name: gap.customer || t('attention.customer') }),
        tone: 'warning',
        actionLabel: t('attention.addToKnowledge'),
        to,
        action: { kind: 'add-knowledge', to, text: gap.question, conversationKey: gap.conversationKey },
        dismissable: true,
      });
    }
    if (data.knowledge.total > data.knowledge.items.length) {
      out.push(moreRow('knowledge', 'knowledge-gap', data.knowledge.total - data.knowledge.items.length, ROUTES.INBOX_SETTINGS, t));
    }
  }

  return out;
}
